import { useTranslation } from "react-i18next";import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator, PriorityBadge } from '@/modules/wms/components/common/StatusIndicator';
import { BarcodeScanner } from '@/shared/components/scanner/BarcodeScanner';
import { CameraScanner } from '@/shared/components/scanner/CameraScanner';
import { ScanWorkflowDialog } from '@/modules/wms/components/scanner/ScanWorkflowDialog';
import { receivingWorkflowSteps, sampleReceivingItems } from '@/modules/wms/lib/scanWorkflowConfig';
import { receivingAPI, oigAPI } from '@/shared/services/api';
import {
  Search,
  Plus,
  Package,
  Truck,
  Clock,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Calendar,
  ChevronRight,
  ScanLine,
  ClipboardCheck,
  MapPin,
  Loader2,
  Camera,
  Keyboard } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import { toast } from 'sonner';

export const Receiving = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [shipmentStatuses, setShipmentStatuses] = useState({});
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanMode, setScanMode] = useState('manual'); // 'manual' or 'camera'
  const [stats, setStats] = useState({ all: 0, inProgress: 0, pending: 0, issues: 0 });

  // Fetch shipments from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [shipmentsData, statsData] = await Promise.all([
        oigAPI.getASNList(),
        receivingAPI.getStats()]
        );
        setShipments(shipmentsData || []);
        setStats({
          all: shipmentsData?.length || 0,
          inProgress: statsData?.in_progress || shipmentsData?.filter((s) => s.status === 'receiving')?.length || 0,
          pending: statsData?.pending || shipmentsData?.filter((s) => s.status === 'pending')?.length || 0,
          issues: statsData?.with_issues || shipmentsData?.filter((s) => s.status === 'issue')?.length || 0
        });
      } catch (error) {
        console.error('Failed to fetch receiving data:', error);
        toast.error('Failed to load shipments');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const upcomingCutoffDate = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
  }, []);

  const upcomingDeliveries = useMemo(() => {
    return shipments.filter(
      (s) => s.status === 'pending' && new Date(s.expected_date || s.expectedDate) <= upcomingCutoffDate
    );
  }, [shipments, upcomingCutoffDate]);

  // Handle quick scan from the main scanner
  const handleQuickScan = async (result) => {
    if (result.valid) {
      // Check if it's an NF-e/CT-e scan
      if (result.type === 'NFE_ACCESS_KEY' || result.type === 'CTE_ACCESS_KEY') {
        // Find matching shipment (use first pending shipment)
        const matchingShipment = shipments.find((s) => s.status === 'pending');
        if (matchingShipment) {
          setSelectedShipment(matchingShipment);
          setShowWorkflowDialog(true);
          toast.success('ASN Retrieved', {
            description: `Found shipment ${matchingShipment.id || matchingShipment.shipment_number} from ${matchingShipment.supplier || matchingShipment.vendor_name}`
          });
        } else {
          toast.info('No Pending Shipments', {
            description: 'No matching ASN found for this document'
          });
        }
      } else {
        toast.info('Scan Recognized', {
          description: `${result.type}: ${result.raw.substring(0, 30)}...`
        });
      }
    }
  };

  // Start receiving workflow for a shipment
  const handleStartReceiving = (shipment) => {
    setSelectedShipment(shipment);
    setShowWorkflowDialog(true);
  };

  // Handle workflow completion
  const handleWorkflowComplete = async (data) => {
    if (selectedShipment) {
      try {
        const shipmentId = selectedShipment.id || selectedShipment.shipment_number;
        await receivingAPI.completeReceiving({ shipment_id: shipmentId });
        setShipmentStatuses((prev) => ({
          ...prev,
          [shipmentId]: 'completed'
        }));
        // Refresh shipments
        const shipmentsData = await oigAPI.getASNList();
        setShipments(shipmentsData || []);
        toast.success('Receiving Complete', {
          description: `${data.scannedItems.length} items received for ${shipmentId}`
        });
      } catch (error) {
        console.error('Failed to complete receiving:', error);
        toast.error('Failed to complete receiving');
      }
    }
  };

  // Handle step completion
  const handleStepComplete = (stepIndex, scanResult) => {
    const step = receivingWorkflowSteps[stepIndex];

    if (step.triggersAction === 'load_asn') {
      toast.info('ASN Loaded', {
        description: 'Expected items loaded from invoice'
      });
    } else if (step.triggersAction === 'validate_item') {
      const gtin = scanResult.fields?.GTIN?.parsed;
      toast.success('Item Validated', {
        description: `GTIN: ${gtin || scanResult.raw}`
      });
    } else if (step.triggersAction === 'assign_location') {
      toast.success('Location Assigned', {
        description: `Items assigned to ${scanResult.raw}`
      });
    }
  };

  // Get effective status (considering local overrides)
  const getShipmentStatus = (shipment) => {
    const shipmentId = shipment.id || shipment.shipment_number;
    return shipmentStatuses[shipmentId] || shipment.status;
  };

  return (
    <div className="space-y-6" data-testid="wms-receiving">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("receiving", "Receiving")}</h1>
          <p className="text-muted-foreground mt-1">{t("scanIncomingShipments", "Scan incoming shipments and match expected invoices")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowWorkflowDialog(true)}>
            <ScanLine className="h-4 w-4" />{t("startScanWorkflow", "Start Scan Workflow")}

          </Button>
          <Button className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
            <Plus className="h-4 w-4" />{t("newShipment", "New Shipment")}

          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("allShipments", "All Shipments")}</p>
              <p className="text-2xl font-bold">{stats.all}</p>
            </div>
            <Package className="h-8 w-8 text-muted-foreground/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("inProgress", "In Progress")}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
            <Truck className="h-8 w-8 text-amber-500/30" />
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">{t("issues", "Issues")}</p>
              <p className="text-2xl font-bold text-red-600">{stats.issues}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500/30" />
          </CardContent>
        </Card>
      </div>

      {/* Scan-Gated Receiving Input */}
      <Card className="card-elevated bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-950/20">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <ScanLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">{t("scangatedReceiving", "Scan-Gated Receiving")}</h3>
              <p className="text-sm text-muted-foreground">{t("scanNfecteTo", "Scan NF-e/CT-e to retrieve ASN and start receiving workflow")}

              </p>
            </div>
            <Badge variant="outline" className="ml-auto gap-1">
              <FileText className="h-3 w-3" />{t("nfecteRequired", "NF-e/CT-e Required")}

            </Badge>
          </div>
          
          <Tabs value={scanMode} onValueChange={setScanMode} className="mb-4">
            <TabsList className="grid w-full max-w-xs grid-cols-2">
              <TabsTrigger value="manual" className="gap-2">
                <Keyboard className="h-4 w-4" />{t("manual", "Manual")}

              </TabsTrigger>
              <TabsTrigger value="camera" className="gap-2">
                <Camera className="h-4 w-4" />{t("camera", "Camera")}

              </TabsTrigger>
            </TabsList>
          </Tabs>
          
          {scanMode === 'manual' ?
          <BarcodeScanner
            onScan={handleQuickScan}
            scanMode="nfe"
            placeholder="Scan NF-e/CT-e invoice barcode to start receiving..."
            showSamples /> :


          <CameraScanner
            onScan={(barcode) => handleQuickScan({ valid: true, type: 'NFE_ACCESS_KEY', raw: barcode })}
            showControls={true} />

          }
        </CardContent>
      </Card>

      {/* Workflow Steps Guide */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <h4 className="font-medium mb-3">{t("scangatedReceivingProcess", "Scan-Gated Receiving Process")}</h4>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
              <span>{t("scanNfecte", "Scan NF-e/CT-e")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs font-bold">2</div>
              <span>{t("scanEachItem", "Scan Each Item")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-violet-500 text-white flex items-center justify-center text-xs font-bold">3</div>
              <span>{t("assignLocation", "Assign Location")}</span>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <span>{t("complete", "Complete")}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts */}
      {upcomingDeliveries.length > 0 &&
      <Card className="card-elevated border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50">
              <Calendar className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium">{t("upcomingDeliveries", "Upcoming Deliveries")}</h4>
              <p className="text-sm text-muted-foreground">
                {upcomingDeliveries.length}{t("shipmentsScheduledFor", "shipment(s) scheduled for the next 7 days")}
            </p>
            </div>
            <Button variant="outline" size="sm">{t("viewSchedule", "View Schedule")}</Button>
          </CardContent>
        </Card>
      }

      {stats.issues > 0 &&
      <Card className="card-elevated border-l-4 border-l-red-500 bg-red-50/50 dark:bg-red-950/20">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium text-red-700 dark:text-red-400">{t("shipmentIssuesDetected", "Shipment Issues Detected")}</h4>
              <p className="text-sm text-muted-foreground">
                {stats.issues}{t("shipmentsRequireAttention", "shipment(s) require attention due to discrepancies")}
            </p>
            </div>
            <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50">{t("reviewIssues", "Review Issues")}

          </Button>
          </CardContent>
        </Card>
      }

      {/* Shipments Tabs */}
      <Tabs defaultValue="expected" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="expected" className="gap-2">
            <Package className="h-4 w-4" />{t("expectedShipments", "Expected Shipments")}

          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <ClipboardCheck className="h-4 w-4" />{t("shipmentHistory", "Shipment History")}

          </TabsTrigger>
        </TabsList>

        <TabsContent value="expected" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg">{t("expectedShipments", "Expected Shipments")}</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search shipments..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10" />
                  
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("shipmentId", "Shipment ID")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("invoice", "Invoice #")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("supplier", "Supplier")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("expectedDate", "Expected Date")}</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("items", "Items")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("priority", "Priority")}</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ?
                    <tr>
                        <td colSpan={8} className="py-8 text-center">
                          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                          <p className="text-sm text-muted-foreground mt-2">{t("loadingShipments", "Loading shipments...")}</p>
                        </td>
                      </tr> :
                    shipments.length === 0 ?
                    <tr>
                        <td colSpan={8} className="py-8 text-center">
                          <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                          <p className="text-sm text-muted-foreground">{t("noShipmentsFound", "No shipments found")}</p>
                        </td>
                      </tr> :
                    shipments.map((shipment) => {
                      const shipmentId = shipment.id || shipment.shipment_number;
                      const effectiveStatus = getShipmentStatus(shipment);
                      return (
                        <tr key={shipmentId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                          <td className="py-4 px-4">
                            <span className="font-mono text-sm font-medium">{shipmentId}</span>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm">{shipment.invoiceNumber || shipment.nfe_key || '-'}</span>
                          </td>
                          <td className="py-4 px-4">
                            <div>
                              <p className="text-sm font-medium">{shipment.supplier || shipment.vendor_name}</p>
                              <p className="text-xs text-muted-foreground">{shipment.carrier || '-'}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <span className="text-sm">{shipment.expectedDate || shipment.expected_date || '-'}</span>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <span className="font-medium">{shipment.receivedItems || shipment.received_qty || 0}</span>
                              <span className="text-muted-foreground">/</span>
                              <span>{shipment.expectedItems || shipment.expected_qty || 0}</span>
                            </div>
                          </td>
                          <td className="py-4 px-4">
                            <PriorityBadge priority={shipment.priority || 'medium'} />
                          </td>
                          <td className="py-4 px-4">
                            <StatusIndicator status={effectiveStatus} />
                          </td>
                          <td className="py-4 px-4 text-right">
                            <Button
                              size="sm"
                              className={
                              effectiveStatus === 'pending' ?
                              'bg-blue-600 hover:bg-blue-700' :
                              effectiveStatus === 'in_progress' || effectiveStatus === 'receiving' ?
                              'bg-amber-600 hover:bg-amber-700' :
                              ''
                              }
                              variant={effectiveStatus === 'completed' || effectiveStatus === 'issue' ? 'outline' : 'default'}
                              onClick={() => handleStartReceiving(shipment)}
                              data-testid={`process-shipment-${shipmentId}`}>
                              
                              {effectiveStatus === 'pending' &&
                              <>
                                  <ScanLine className="h-4 w-4 mr-1" />{t("scanReceive", "Scan & Receive")}

                              </>
                              }
                              {(effectiveStatus === 'in_progress' || effectiveStatus === 'receiving') && 'Continue'}
                              {effectiveStatus === 'completed' && 'View'}
                              {effectiveStatus === 'issue' && 'Review'}
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </td>
                        </tr>);

                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="card-elevated">
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
              <h3 className="font-semibold mb-2">{t("shipmentHistory", "Shipment History")}</h3>
              <p className="text-sm text-muted-foreground mb-4">{t("viewCompletedAnd", "View completed and archived shipment records")}

              </p>
              <Button variant="outline">{t("loadHistory", "Load History")}</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Scan Workflow Dialog */}
      <ScanWorkflowDialog
        open={showWorkflowDialog}
        onOpenChange={setShowWorkflowDialog}
        title={selectedShipment ? `Receiving: ${selectedShipment.id}` : 'Receiving Workflow'}
        icon={Truck}
        steps={receivingWorkflowSteps}
        expectedItems={sampleReceivingItems}
        onComplete={handleWorkflowComplete}
        onStepComplete={handleStepComplete}
        validationMode="flexible" />
      
    </div>);

};